import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

const inputCls =
  "w-full bg-transparent border-b border-[#E0E3E6] focus:border-[#000000] outline-none py-2.5 text-sm text-[#000000] placeholder:text-[#8A94A0] transition-colors";

export const SearchBar = ({ compact = false }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [zones, setZones] = useState([]);
  const { t } = useLang();
  const [f, setF] = useState({
    contract: params.get("contract") || "",
    category: params.get("category") || "",
    zone: params.get("zone") || "",
    max_price: params.get("max_price") || "",
    min_mq: params.get("min_mq") || "",
    rooms: params.get("rooms") || "",
  });

  useEffect(() => {
    api.get("/meta/filters").then((r) => setZones(r.data.zones || [])).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    navigate(`/immobili?${q.toString()}`);
  };

  const labelCls = "text-[10px] font-semibold uppercase tracking-[0.2em] text-[#000000]";

  return (
    <form
      onSubmit={submit}
      data-testid="property-search-form"
      className={`bg-white/75 backdrop-blur-xl border border-white/60 shadow-[0_24px_60px_-24px_rgba(16,21,27,0.25)] p-5 sm:p-6 ${
        compact ? "" : "rounded-sm"
      }`}
    >
      <div className={`grid gap-4 ${compact ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-7" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"}`}>
        <label className="block">
          <span className={labelCls}>{t("search.contratto")}</span>
          <select data-testid="filter-contract" value={f.contract} onChange={(e) => set("contract", e.target.value)} className={inputCls}>
            <option value="">{t("search.tutti")}</option>
            <option value="vendita">{t("card.vendita")}</option>
            <option value="affitto">{t("card.affitto")}</option>
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>{t("search.tipologia")}</span>
          <select data-testid="filter-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
            <option value="">{t("search.tutte")}</option>
            {CATEGORIES.map((c, i) => <option key={c.value} value={c.value}>{t(`fcats.${i}`)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>{t("search.zona")}</span>
          <select data-testid="filter-zone" value={f.zone} onChange={(e) => set("zone", e.target.value)} className={inputCls}>
            <option value="">{t("search.zonaAll")}</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>{t("search.prezzo")}</span>
          <input data-testid="filter-max-price" type="number" min="0" value={f.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder={`${t("ph")} 1.500.000`} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>{t("search.mq")}</span>
          <input data-testid="filter-min-mq" type="number" min="0" value={f.min_mq} onChange={(e) => set("min_mq", e.target.value)} placeholder={`${t("ph")} 100`} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>{t("search.locali")}</span>
          <input data-testid="filter-rooms" type="number" min="0" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} placeholder={`${t("ph")} 3`} className={inputCls} />
        </label>
        <div className="flex items-end col-span-2 md:col-span-1">
          <button
            type="submit"
            data-testid="search-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-[#000000] hover:bg-[#000000] text-white text-sm font-medium px-6 py-3 transition-colors duration-300"
          >
            <Search size={15} /> {t("search.cerca")}
          </button>
        </div>
      </div>
    </form>
  );
};
